import React, { useState, useEffect, useRef } from 'react';
import { Box, Badge, IconButton, Popover, Typography, Divider, CircularProgress } from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const POLL_MS = 30000;

const typeIcon = (type) => {
  if (type === 'proposal_submitted' || type === 'proposal_accepted') return <DescriptionIcon fontSize="small" />;
  if (type === 'contract_signed' || type === 'contract_created') return <AssignmentTurnedInIcon fontSize="small" />;
  if (type === 'invoice_submitted' || type === 'invoice_approved' || type === 'invoice_paid') return <ReceiptIcon fontSize="small" />;
  return <MailOutlineIcon fontSize="small" />;
};

const typeColors = (type) => {
  if (type.startsWith('proposal')) return { bg: '#B5D4F4', fg: '#0C447C' };
  if (type.startsWith('contract')) return { bg: '#9FE1CB', fg: '#085041' };
  if (type.startsWith('invoice')) return { bg: '#FAC775', fg: '#633806' };
  return { bg: '#e0e0e0', fg: '#555' };
};

const timeAgo = (iso) => {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  const days = Math.floor(hrs / 24);
  if (days < 7) return days + 'd ago';
  return new Date(iso).toLocaleDateString();
};

export default function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const fetchCount = async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count || 0);
    } catch (err) { /* silent */ }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications?limit=10');
      setItems(res.data || []);
    } catch (err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCount();
    timerRef.current = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchList();
  };
  const handleClose = () => setAnchorEl(null);

  const handleItemClick = async (item) => {
    if (!item.read_at) {
      try { await api.put('/notifications/' + item.id + '/read'); } catch (e) {}
      setUnreadCount(c => Math.max(0, c - 1));
      setItems(prev => prev.map(p => p.id === item.id ? { ...p, read_at: new Date().toISOString() } : p));
    }
    handleClose();
    if (item.link_path) navigate(item.link_path);
  };

  const handleMarkAllRead = async () => {
    try { await api.put('/notifications/read-all'); } catch (e) {}
    setUnreadCount(0);
    setItems(prev => prev.map(p => ({ ...p, read_at: p.read_at || new Date().toISOString() })));
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton onClick={handleOpen} sx={{ color: '#555' }} aria-label="Notifications">
        <Badge badgeContent={unreadCount} color="error" overlap="circular">
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>
      <Popover open={open} anchorEl={anchorEl} onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxHeight: 480, borderRadius: 2, mt: 1 } }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700, color: '#1a237e' }}>Notifications</Typography>
          {unreadCount > 0 && (
            <Typography onClick={handleMarkAllRead}
              sx={{ fontSize: 12, color: '#1565c0', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
              Mark all as read
            </Typography>
          )}
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
            <Typography sx={{ color: '#999', fontSize: 13 }}>No notifications yet</Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
            {items.map(item => {
              const c = typeColors(item.type);
              const isUnread = !item.read_at;
              return (
                <Box key={item.id} onClick={() => handleItemClick(item)}
                  sx={{ p: 1.5, cursor: 'pointer', display: 'flex', gap: 1.25,
                    borderBottom: '1px solid #f5f6fa',
                    bgcolor: isUnread ? '#E6F1FB' : 'white',
                    opacity: isUnread ? 1 : 0.75,
                    '&:hover': { bgcolor: isUnread ? '#d3e4f7' : '#f5f6fa' } }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: c.bg, color: c.fg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {typeIcon(item.type)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: isUnread ? 700 : 500, color: '#1a237e' }}>
                      {item.title}
                    </Typography>
                    {item.body && (
                      <Typography sx={{ fontSize: 12, color: '#555', lineHeight: 1.4, mt: 0.25 }}>
                        {item.body}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 11, color: '#999', mt: 0.5 }}>
                      {timeAgo(item.created_at)}
                    </Typography>
                  </Box>
                  {isUnread && <Box sx={{ width: 8, height: 8, bgcolor: '#1976d2', borderRadius: '50%', mt: 1, flexShrink: 0 }} />}
                </Box>
              );
            })}
          </Box>
        )}
        <Divider />
        <Box onClick={() => { handleClose(); navigate('/notifications'); }}
          sx={{ p: 1.25, textAlign: 'center', cursor: 'pointer', bgcolor: '#f5f6fa',
            '&:hover': { bgcolor: '#e8eaf6' } }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1565c0' }}>
            View all notifications →
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
